import ITask from './ITask';
import { Alpha, OverlayFadeIn, OverlayFadeOut } from 'System/Animation/Clip';
import { PlayAnimation, StartAnimationClip } from 'System/Animation/AnimationHelper';
import { GameObjectEvent } from 'GameObject/GameObject';
import { RenderSystemEvent } from 'System/Render/RenderSystem';
import OverlayComponent from 'Component/UI/Overlay';
import FarmSystem from 'System/Farm/FarmSystem';
import Game from 'Game';
import FarmUISystem from 'System/UI/FarmUISystem';
import { PetFTUXLayout } from 'Layouts/Farm/PetFTUXLayout';
import RelativePositionComponent from 'Component/Render/RelativePosition';
import TextComponent from 'Component/Render/Text';
import TransformComponent from 'Component/Transform';
import { FTUXType } from 'Player/PlayerProperty/Progress/FTUXType';
import { PlayerActions } from 'State/Player/PlayerActions';
import { getLayout } from 'Utility/layout/LayoutHelper';

export default class FarmPetFTUXTask implements ITask<FarmSystem> {
    public *execute(system: FarmSystem): IterableIterator<any> {
        let nextScreen: boolean = false;
        // Overlay setup
        system.scene.emit(RenderSystemEvent.BRING_TO_FRONT, system.animator);
        const overlay = system.animator.getChildByName('overlay');
        const queueNextFTUXStep = () => {
            nextScreen = true;
        };

        // Pet initial setup
        const layout = getLayout(PetFTUXLayout);
        layout.root.addComponent(RelativePositionComponent, { x: -0.45, y: -0.1 });

        const pet = system.scene.getSystem(FarmUISystem).map.pet;
        const petParent = pet.parent;
        pet.addChild(layout.root);
        system.animator.addChild(pet);

        // Fix ftux size cause pet was reduced to fill in the farm
        layout.root.getComponent(TransformComponent).scale.set(2);

        pet.once(GameObjectEvent.SELECTED, queueNextFTUXStep);

        // Get all objects and components needed
        const ftuxTextComponent = layout.ftuxText.getComponent(TextComponent);

        const ftuxSteps: (() => IterableIterator<any>)[] = [
            showInitialDialogOverlay,
            hideOverlayAndWait,
            promptPetSleeping,
        ];

        for (let i = 0; i < ftuxSteps.length; i++) {
            // clear interaction from previous step
            nextScreen = false;

            yield* ftuxSteps[i]();

            // Wait for user interaction
            while (!nextScreen) {
                yield;
            }
        }

        // Fade out ftux
        StartAnimationClip(
            new Alpha(layout.root, {
                startAlpha: 1,
                endAlpha: 0,
                duration: 300,
            })
        );

        yield* PlayAnimation(new OverlayFadeOut(overlay));

        // Remove pet from overlay
        petParent.addChild(pet);

        layout.root.destroy();

        // Enable interaction with farm map
        overlay.getComponent(OverlayComponent).preventInteraction = false;

        PlayerActions.setFTUXUnlocks([FTUXType.FARM_PET]);
        return;

        function* showInitialDialogOverlay(): IterableIterator<any> {
            // Prevent interaction with farm map
            overlay.getComponent(OverlayComponent).preventInteraction = true;

            // Fade in overlay
            StartAnimationClip(new OverlayFadeIn(overlay));
        }

        // ***** Show second FTUX screen *****
        // ***** Fade out overlay and wait for the mini game to finish *****
        function* hideOverlayAndWait(): IterableIterator<any> {
            overlay.getComponent(OverlayComponent).preventInteraction = false;

            // Remove pet from overlay
            petParent.addChild(pet);

            // Fade out ftux
            StartAnimationClip(
                new Alpha(layout.root, {
                    startAlpha: 1,
                    endAlpha: 0,
                    duration: 300,
                })
            );

            // Fade out overlay
            yield* PlayAnimation(new OverlayFadeOut(overlay));

            system.scene.on('PetSleeping', queueNextFTUXStep);
        }

        // ***** Show last FTUX screen *****
        // ***** Fade in overlay and wait for the player to click on overlay *****
        function* promptPetSleeping(): IterableIterator<any> {
            overlay.getComponent(OverlayComponent).preventInteraction = true;

            // Add pet to overlay
            system.animator.addChild(pet);

            // Fade in ftux
            StartAnimationClip(
                new Alpha(layout.root, {
                    startAlpha: 0,
                    endAlpha: 1,
                    duration: 300,
                })
            );

            // Change to last ftux text
            ftuxTextComponent.text = Game.locale.getText('FarmPetFTUX3');

            // Fade in overlay
            yield* PlayAnimation(new OverlayFadeIn(overlay));

            overlay.once(GameObjectEvent.SELECTED, queueNextFTUXStep);
        }
    }
}
